import { EventType, FailureContext, generateEventTypeMapDefaults, WorkflowEventEventTypeMap, WorkflowEventMetaDataMapWithDefaults } from '@aiwyn/shared';

/**
 * Flow 9 — Scheduled Payments.
 * The user skips or resumes individual scheduled payments — both one-time scheduled payments
 * and the next occurrence of an autopay plan.
 *
 * Each mutation follows the SUBMITTED + SUCCEEDED + FAILED triad. Terminals carry
 * `operationDurationMs`; FAILED metadata extends `FailureContext`. The `*_SUBMITTED` step
 * doubles as funnel-entry for skip / resume — the user clicks the menu item and the mutation
 * fires immediately, so there's no earlier UI gesture worth a separate marker.
 *
 * `SCHEDULED_PAYMENT_UPDATE_*` stages aren't wired today; `SCHEDULED_PAYMENT_UPDATE_FAILED`
 * is intentionally absent — add it alongside the SUBMITTED + SUCCEEDED pair when the update
 * funnel ships.
 */
export enum ScheduledPaymentWorkflowEvent {
  // Optional — emits when the user opens a scheduled payment for review without yet acting on it.
  // SCHEDULED_PAYMENT_VIEWED = 'SCHEDULED_PAYMENT_VIEWED',
  SCHEDULED_PAYMENT_UPDATE_STARTED = 'SCHEDULED_PAYMENT_UPDATE_STARTED',
  SCHEDULED_PAYMENT_UPDATE_SUCCEEDED = 'SCHEDULED_PAYMENT_UPDATE_SUCCEEDED',
  SCHEDULED_PAYMENT_SKIP_SUBMITTED = 'SCHEDULED_PAYMENT_SKIP_SUBMITTED',
  SCHEDULED_PAYMENT_SKIP_SUCCEEDED = 'SCHEDULED_PAYMENT_SKIP_SUCCEEDED',
  SCHEDULED_PAYMENT_SKIP_FAILED = 'SCHEDULED_PAYMENT_SKIP_FAILED',
  SCHEDULED_PAYMENT_RESUME_SUBMITTED = 'SCHEDULED_PAYMENT_RESUME_SUBMITTED',
  SCHEDULED_PAYMENT_RESUME_SUCCEEDED = 'SCHEDULED_PAYMENT_RESUME_SUCCEEDED',
  SCHEDULED_PAYMENT_RESUME_FAILED = 'SCHEDULED_PAYMENT_RESUME_FAILED',
  SCHEDULED_AUTO_PAYMENT_SKIP_SUBMITTED = 'SCHEDULED_AUTO_PAYMENT_SKIP_SUBMITTED',
  SCHEDULED_AUTO_PAYMENT_SKIP_SUCCEEDED = 'SCHEDULED_AUTO_PAYMENT_SKIP_SUCCEEDED',
  SCHEDULED_AUTO_PAYMENT_SKIP_FAILED = 'SCHEDULED_AUTO_PAYMENT_SKIP_FAILED',
  SCHEDULED_AUTO_PAYMENT_RESUME_SUBMITTED = 'SCHEDULED_AUTO_PAYMENT_RESUME_SUBMITTED',
  SCHEDULED_AUTO_PAYMENT_RESUME_SUCCEEDED = 'SCHEDULED_AUTO_PAYMENT_RESUME_SUCCEEDED',
  SCHEDULED_AUTO_PAYMENT_RESUME_FAILED = 'SCHEDULED_AUTO_PAYMENT_RESUME_FAILED',
  SCHEDULED_PAYMENT_CANCELLED = 'SCHEDULED_PAYMENT_CANCELLED',
}

export type ScheduledPaymentWorkflowEventMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  ScheduledPaymentWorkflowEvent,
  {
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_UPDATE_STARTED]: { scheduledPaymentId: string };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_UPDATE_SUCCEEDED]: { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_SUBMITTED]: { scheduledPaymentId: string };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_SUCCEEDED]: { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_FAILED]: FailureContext & { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_SUBMITTED]: { scheduledPaymentId: string };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_SUCCEEDED]: { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_FAILED]: FailureContext & { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_SUBMITTED]: { scheduledPaymentId: string };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_SUCCEEDED]: { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_FAILED]: FailureContext & { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_SUBMITTED]: { scheduledPaymentId: string };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_SUCCEEDED]: { scheduledPaymentId: string; operationDurationMs: number };
    [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_FAILED]: FailureContext & { scheduledPaymentId: string; operationDurationMs: number };
  }
>;

export const ScheduledPaymentEventEventTypeMap: WorkflowEventEventTypeMap<ScheduledPaymentWorkflowEvent> = {
  ...generateEventTypeMapDefaults(ScheduledPaymentWorkflowEvent, EventType.USER),
  [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_UPDATE_SUCCEEDED]: EventType.SYSTEM,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_SUCCEEDED]: EventType.SYSTEM,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_SUCCEEDED]: EventType.SYSTEM,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_SUCCEEDED]: EventType.SYSTEM,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_SUCCEEDED]: EventType.SYSTEM,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_FAILED]: EventType.ERROR,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_FAILED]: EventType.ERROR,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_FAILED]: EventType.ERROR,
  [ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_FAILED]: EventType.ERROR,
};
